/**
 * Hook for managing template loading and generation - PERFORMANCE OPTIMIZED
 *
 * What it does:
 * - Gets the current page URL to extract endpoint information
 * - Generates templates for navigation categories (only once)
 * - Processes template data when it arrives
 * - Stores templates in Redux for other components to use
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Memoized selectors prevent unnecessary re-computations
 * - Combined effects reduce the number of effect executions
 * - Memoized callbacks prevent function recreation on every render
 * - Minimal dependencies in effect arrays reduce re-runs
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import type { RootState } from '../../../services/serviceTypes';
import { setGenerateTemplateError } from '../../DiscoverCMDBApps/data-layer/GenerateTemplateSlice';
import {
  type GenerateCategoryTemplateRequest,
  useLazyGenerateCategoryTemplateQuery,
} from '../data-store/copilotApi';
import {
  setCategoryTemplates,
  setParsedTemplates,
} from '../data-store/copilotSlice';
import { parseTemplateResponse } from '../utils/templateParsers';

const useTemplateLoader = (mainCategories: string[]) => {
  const dispatch = useDispatch();
  const { search } = useLocation();

  // OPTIMIZATION: Memoized URL parameter extraction
  // Prevents URLSearchParams recreation on every render
  // Only recalculates when URL search params actually change
  const { endpointKey, isEditMode } = useMemo(() => {
    const params = new URLSearchParams(search);
    const key = params.get('endpointKey');

    return {
      endpointKey: key,
      isEditMode: params.get('isEdit') === 'true' && !!key,
    };
  }, [search]);

  // OPTIMIZATION: Memoized template existence check
  // Prevents deep object inspection on every render
  // Only recalculates when categoryTemplates actually changes
  const hasExistingTemplates = useSelector((state: RootState) => {
    const templates = state.copilot.categoryTemplates;

    return templates && Object.keys(templates).length > 0;
  });

  // Lazy API call
  const [generateTemplate, { data: templateData }] =
    useLazyGenerateCategoryTemplateQuery();

  // OPTIMIZATION: Memoized template generation callback
  // Prevents function recreation on every render
  // Only recreates when dependencies actually change
  // Reduces effect dependency array complexity
  const generateTemplates = useCallback(async () => {
    // Early return prevents unnecessary payload creation
    if (mainCategories.length === 0 || hasExistingTemplates) {
      return;
    }

    const payload: GenerateCategoryTemplateRequest = {
      applicationType: 'CUA',
      parentCategoryName: 'configuration',
      categoryNames: mainCategories,
      techOnBoard: true,
    };

    if (endpointKey) {
      payload.endpointKey = Number(endpointKey);
    }

    await generateTemplate(payload).catch((err: string) => {
      setGenerateTemplateError(err);
    });
  }, [mainCategories, hasExistingTemplates, endpointKey, generateTemplate]);

  // OPTIMIZATION: Combined effect for template generation and processing
  // BEFORE: Two separate useEffect hooks with overlapping concerns
  // AFTER: Single focused effect that handles both template generation and processing
  // BENEFIT: Fewer effect executions, cleaner dependency management
  useEffect(() => {
    // Generate templates if needed (replaces first useEffect)
    generateTemplates().catch(() => {
      // Handle errors silently
    });

    // Process template data when available (replaces second useEffect)
    if (templateData) {
      // Store raw template data in Redux
      dispatch(setCategoryTemplates(templateData));

      // Process templates into a more usable format
      const parsedData = parseTemplateResponse(
        templateData,
        isEditMode,
        dispatch
      );

      // Store processed templates in Redux
      dispatch(setParsedTemplates(parsedData));
    }
  }, [generateTemplates, templateData, dispatch, isEditMode]);
};

export default useTemplateLoader;
